<script setup lang="ts">
// "Sign in with Dabble": where another of our sites (the Circle community)
// sends someone to sign in. The router has already made sure they are signed
// in here, bouncing through the login form and back if not. This page asks the
// API to approve the sign-in for the active account and sends the browser back
// to the site with a one-time code. There is nothing to click when it works.
//
// The API decides everything that matters: which sites may ask, where a
// sign-in may return to, and that the account's email is verified. This page
// only follows the address it hands back.
import AuthLayout from '@/components/AuthLayout/AuthLayout.vue';
import { useI18n } from '@/i18n';
import { logError } from '@/lib/logError';
import rest from '@/lib/rest';
import { useUserStore } from '@/stores/user';
import { useToast } from '@/use/toast';
import { Button, Heading, LayoutGap, Loader, Prose } from '@dabble/quillui';
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';

const { t } = useI18n();
const route = useRoute();
const userStore = useUserStore();
const { showToast } = useToast();

type Status = 'loading' | 'verify' | 'invalid' | 'error';
const status = ref<Status>('loading');
const isSendingVerification = ref(false);

// The request itself is wrong (a site we don't know, an address that isn't on
// file): trying again can't fix it, only starting over from the site can.
const INVALID_REQUEST = new Set(['UNKNOWN_CLIENT', 'REDIRECT_MISMATCH', 'UNSUPPORTED_RESPONSE_TYPE', 'INVALID_STATE']);

const param = (name: string) => {
  const value = route.query[name];
  return typeof value === 'string' ? value : undefined;
};

async function approve() {
  status.value = 'loading';
  const request: Record<string, string> = {};
  for (const name of ['response_type', 'client_id', 'redirect_uri', 'state']) {
    const value = param(name);
    if (value !== undefined) request[name] = value;
  }
  try {
    const { redirect } = await rest
      .post('oauth/authorize')
      .header('x-uid', userStore.user.uid || '')
      .credentials('include')
      .send(request);
    // replace: the back button from the site should not land on a spent request.
    window.location.replace(redirect);
  } catch (err) {
    const code = err instanceof Error ? err.message : '';
    if (code === 'EMAIL_NOT_VERIFIED') {
      status.value = 'verify';
    } else if (INVALID_REQUEST.has(code)) {
      status.value = 'invalid';
    } else {
      logError('oauth authorize failed', err);
      status.value = 'error';
    }
  }
}

async function onSendVerification() {
  if (isSendingVerification.value) return;
  isSendingVerification.value = true;
  try {
    await userStore.sendVerification();
    showToast('success', t('account_verify_email_sent_toast'));
  } catch {
    showToast('danger', t('account_verify_email_failed_toast'));
  } finally {
    isSendingVerification.value = false;
  }
}

onMounted(approve);
</script>

<template>
  <AuthLayout>
    <LayoutGap direction="column" size="medium" align="center" class="oauth-authorize">
      <template v-if="status === 'loading'">
        <Loader />
        <Prose align="center">{{ t('oauth_authorize_loading') }}</Prose>
      </template>

      <template v-else-if="status === 'verify'">
        <Heading :level="3" :margin="false">{{ t('oauth_authorize_verify_title') }}</Heading>
        <Prose align="center">{{ t('oauth_authorize_verify_description', { email: userStore.user.email }) }}</Prose>
        <Button fill-width size="large" @click="approve">{{ t('oauth_authorize_verify_continue') }}</Button>
        <Button
          fill-width
          size="large"
          variant="secondary"
          finish="outlined"
          :disabled="isSendingVerification"
          @click="onSendVerification"
        >
          {{ isSendingVerification ? t('account_verify_email_sending') : t('account_verify_email_button') }}
        </Button>
      </template>

      <template v-else-if="status === 'invalid'">
        <Heading :level="3" :margin="false">{{ t('oauth_authorize_invalid_title') }}</Heading>
        <Prose align="center">{{ t('oauth_authorize_invalid_description') }}</Prose>
      </template>

      <template v-else>
        <Heading :level="3" :margin="false">{{ t('oauth_authorize_error_title') }}</Heading>
        <Prose align="center">{{ t('oauth_authorize_error_description') }}</Prose>
        <Button fill-width size="large" @click="approve">{{ t('oauth_authorize_retry') }}</Button>
      </template>
    </LayoutGap>
  </AuthLayout>
</template>

<style lang="scss" scoped>
.oauth-authorize {
  width: 100%;
  text-align: center;
}
</style>
